import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TenantConfigService } from 'src/app/shared/services/tenant-config.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { UserService } from 'src/app/core/common-services/user.services';
import { LanguageService } from 'src/app/shared/services/language.service';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { FaqDataService } from 'src/app/core/data-services/faq/faq.data.service';
import { JariValidators } from 'src/app/shared/shared/jari-validators';
import { hasError } from 'src/app/core/base/base';
import { InvitationRegistrationDataService } from 'src/app/core/data-services/identity/invitation-registration.data.service';
import {
  InvitationDetailsDto,
  InvitationLinkResponseDto,
  InvitationLinkStatusType,
} from 'src/app/core/data-models/invitation-registration.data.models';

@Component({
  selector: 'app-complete-register',
  templateUrl: './complete-register.component.html',
  styleUrls: ['./complete-register.component.scss'],
})
export class CompleteRegisterComponent implements OnInit, OnDestroy {
  @ViewChild('termsDialogRef') termsDialogRef!: TemplateRef<any>;

  companyName = '';
  token = '';

  // Link status state & API metadata
  invitationData: InvitationLinkResponseDto | null = null;
  isLoadingLink = true;
  linkStatus:
    | InvitationLinkStatusType
    | 'Valid'
    | 'Expired'
    | 'Cancelled'
    | 'Invalid'
    | 'Used'
    | null = null;
  linkMessage = '';
  logoUrl = '';
  primaryColor = '';
  steps: string[] = ['verify', 'confirm', 'password', 'terms', 'activate'];

  // Stepper state: 1 = Verify Identity (OTP), 2 = Confirm Details, 3 = Create Password & Activate
  currentStep = 1;

  // Step 1: OTP State
  otpLength = 6;
  otpDigits: string[] = ['', '', '', '', '', ''];
  isSendingOtp = false;
  isVerifyingOtp = false;
  isOtpVerified = false;
  maskedPhoneNumber = '';
  wrongAttempts = 0;
  readonly maxWrongAttempts = 5;

  // Timers
  expirationSeconds = 300; // default 5 minutes validity
  resendCooldownSeconds = 60; // default 60 seconds cooldown
  private defaultExpirationSeconds = 300;
  private defaultResendCooldownSeconds = 60;
  private expirationIntervalId: any;
  private resendIntervalId: any;

  // Step 2: Details State
  isLoadingDetails = false;
  details: InvitationDetailsDto = {};
  showSupportInfo = false;

  // Step 3: Password & Terms State
  passwordForm!: FormGroup;
  hidePassword = true;
  hideConfirmPassword = true;
  acceptTerms = false;
  isActivating = false;
  hasError = hasError;

  passwordRules = {
    minChars: false,
    hasCapital: false,
    hasNumber: false,
    hasSpecial: false,
    matches: false,
  };

  // Terms Dialog State
  termsData: any = null;
  isLoadingTerms = false;
  private termsDialogRefInstance?: MatDialogRef<any>;

  constructor(
    public tenantConfigService: TenantConfigService,
    public languageService: LanguageService,
    private invitationService: InvitationRegistrationDataService,
    private notificationService: NotificationService,
    private userService: UserService,
    private signalrService: SignalrService,
    private faqDataService: FaqDataService,
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.companyName =
      localStorage.getItem('currentLang') === 'ar' &&
      this.tenantConfigService.tenant.companyNameAr
        ? this.tenantConfigService.tenant.companyNameAr
        : this.tenantConfigService.tenant.companyName || 'Miyar';
  }

  ngOnInit(): void {
    // Extract token from route parameters or query parameters
    this.token =
      this.route.snapshot.paramMap.get('token') ||
      this.route.snapshot.paramMap.get('otp') ||
      this.route.snapshot.queryParams['token'] ||
      '';

    console.log(this.token);

    this.initPasswordForm();

    if (!this.token) {
      this.isLoadingLink = false;
      this.linkStatus = 'Invalid';
      this.linkMessage = this.getFallbackMessage('Invalid');
      return;
    }

    this.validateInvitationLink();
  }

  ngOnDestroy(): void {
    this.clearAllTimers();
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  // ==========================================================
  // LINK VALIDATION
  // ==========================================================

  validateInvitationLink(): void {
    this.isLoadingLink = true;
    this.invitationService.checkLink(this.token).subscribe({
      next: (res) => {
        this.isLoadingLink = false;
        const linkData = this.extractLinkData(res);
        this.invitationData = linkData;

        // Apply tenant / branding data if provided in response
        this.applyLinkDataBranding(linkData);

        const rawStatus = (
          linkData.Status ||
          linkData.status ||
          (res as any)?.status ||
          ''
        )
          .toString()
          .trim();

        const statusKey = this.normalizeStatus(rawStatus);

        const redirectTo = linkData.RedirectTo || linkData.redirectTo;
        if (redirectTo) {
          if (
            redirectTo.startsWith('http://') ||
            redirectTo.startsWith('https://')
          ) {
            window.location.href = redirectTo;
          } else {
            this.router.navigateByUrl('/authentication/login');
          }
          this.notificationService.error(res.data?.message || '');

          return;
        }

        if (statusKey === 'Valid') {
          this.linkStatus = 'Valid';
          this.currentStep = 1;
          const phone =
            linkData.MaskedPhone ||
            linkData.maskedPhone ||
            linkData.MaskedPhoneNumber ||
            linkData.maskedPhoneNumber ||
            linkData.phoneNumber ||
            (linkData as any).PhoneNumber;
          if (phone) {
            this.maskedPhoneNumber = phone;
          }

          // Configure OTP length & timers from API response if provided
          this.configureOtpSettings(linkData);

          // Automatically trigger sending the OTP code
          this.sendOtp();
        } else if (statusKey === 'Used') {
          this.linkStatus = 'Used';
          this.router.navigate(['/authentication/login']);
        } else if (statusKey === 'Expired') {
          this.linkStatus = 'Expired';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Expired');
          this.linkMessage = this.formatSupportMessage(msg, 'Expired');
        } else if (statusKey === 'Cancelled') {
          this.linkStatus = 'Cancelled';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Cancelled');
          this.linkMessage = this.formatSupportMessage(msg, 'Cancelled');
        } else {
          this.linkStatus = 'Invalid';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Invalid');
          this.linkMessage = this.formatSupportMessage(msg, 'Invalid');
        }
      },
      error: (err) => {
        this.isLoadingLink = false;
        const errorData = this.extractLinkData(err?.error);
        if (errorData) {
          this.invitationData = errorData;
          this.applyLinkDataBranding(errorData);
        }

        const rawStatus = (
          errorData.Status ||
          errorData.status ||
          err?.error?.status ||
          ''
        )
          .toString()
          .trim();
        const statusKey = this.normalizeStatus(rawStatus);

        const redirectTo = errorData.RedirectTo || errorData.redirectTo;
        if (redirectTo) {
          if (
            redirectTo.startsWith('http://') ||
            redirectTo.startsWith('https://')
          ) {
            window.location.href = redirectTo;
          } else {
            this.router.navigateByUrl(redirectTo);
          }
          return;
        }

        if (statusKey === 'Used') {
          this.linkStatus = 'Used';
          this.router.navigate(['/authentication/login']);
          return;
        }

        if (statusKey === 'Expired') {
          this.linkStatus = 'Expired';
          const msg =
            errorData.Message ||
            errorData.message ||
            err?.error?.message ||
            this.getFallbackMessage('Expired');
          this.linkMessage = this.formatSupportMessage(msg, 'Expired');
          return;
        }

        if (statusKey === 'Cancelled') {
          this.linkStatus = 'Cancelled';
          const msg =
            errorData.Message ||
            errorData.message ||
            err?.error?.message ||
            this.getFallbackMessage('Cancelled');
          this.linkMessage = this.formatSupportMessage(msg, 'Cancelled');
          return;
        }

        this.linkStatus = 'Invalid';
        const msg =
          errorData.Message ||
          errorData.message ||
          err?.error?.message ||
          this.getFallbackMessage('Invalid');
        this.linkMessage = this.formatSupportMessage(msg, 'Invalid');
      },
    });
  }

  private extractLinkData(res: any): InvitationLinkResponseDto {
    if (!res) return {} as InvitationLinkResponseDto;
    return res.data || res.resource || res;
  }

  private applyLinkDataBranding(linkData: InvitationLinkResponseDto): void {
    if (!linkData) return;

    const clientName = linkData.ClientName || linkData.clientName;
    if (clientName) {
      this.companyName = clientName;
    }

    const logo = linkData.LogoUrl || linkData.logoUrl;
    if (logo) {
      this.logoUrl = logo;
      if (this.tenantConfigService?.tenant) {
        this.tenantConfigService.tenant.coloredLogoUrl = logo;
      }
    }

    const primaryCol = linkData.PrimaryColor || linkData.primaryColor;
    if (primaryCol) {
      this.primaryColor = primaryCol;
      document.documentElement.style.setProperty(
        '--invitation-primary-color',
        primaryCol,
      );
    }

    const steps = linkData.Steps || linkData.steps;
    if (steps && Array.isArray(steps) && steps.length > 0) {
      this.steps = steps;
    }

    const email = linkData.SupportEmail || linkData.supportEmail;
    if (email && this.tenantConfigService?.tenant) {
      this.tenantConfigService.tenant.supportEmail = email;
    }

    const phone = linkData.SupportPhone || linkData.supportPhone;
    if (phone && this.tenantConfigService?.tenant) {
      this.tenantConfigService.tenant.tollFreeNumber = phone;
    }

    const invLang = linkData.InvitationLanguage || linkData.invitationLanguage;
    if (invLang && (invLang === 'ar' || invLang === 'en')) {
      if (this.languageService.currentLanguage !== invLang) {
        this.languageService.setLanguage(invLang);
      }
    }
  }

  private configureOtpSettings(linkData: InvitationLinkResponseDto): void {
    const otpLen = linkData.OtpLength ?? linkData.otpLength ?? 6;
    if (otpLen && otpLen > 0) {
      this.otpLength = otpLen;
      if (this.otpDigits.length !== otpLen) {
        this.otpDigits = new Array(otpLen).fill('');
      }
    }

    const validMinutes = linkData.OtpValidMinutes ?? linkData.otpValidMinutes;
    if (
      validMinutes != null &&
      !isNaN(Number(validMinutes)) &&
      Number(validMinutes) > 0
    ) {
      this.defaultExpirationSeconds = Number(validMinutes) * 60;
      this.expirationSeconds = this.defaultExpirationSeconds;
    }

    const resendSeconds =
      linkData.OtpResendSeconds ?? linkData.otpResendSeconds;
    if (
      resendSeconds != null &&
      !isNaN(Number(resendSeconds)) &&
      Number(resendSeconds) >= 0
    ) {
      this.defaultResendCooldownSeconds = Number(resendSeconds);
      this.resendCooldownSeconds = this.defaultResendCooldownSeconds;
    }
  }

  private normalizeStatus(
    status: string,
  ): InvitationLinkStatusType | 'Invalid' {
    const s = status.toLowerCase();
    if (s === 'valid') return 'Valid';
    if (s === 'used') return 'Used';
    if (s === 'expired') return 'Expired';
    if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
    return 'Invalid';
  }

  // ==========================================================
  // STEP 1: OTP VERIFICATION
  // ==========================================================

  sendOtp(): void {
    if (this.isSendingOtp) return;
    this.isSendingOtp = true;
    this.otpDigits = new Array(this.otpLength).fill('');
    this.wrongAttempts = 0;

    this.invitationService.sendOtp(this.token).subscribe({
      next: (res) => {
        this.isSendingOtp = false;
        const resData = (res as any)?.data || res;
        const phone =
          (res as any)?.MaskedPhone ||
          (res as any)?.data?.MaskedPhone ||
          res?.data?.maskedPhoneNumber ||
          (res as any)?.maskedPhone;
        if (phone) {
          this.maskedPhoneNumber = phone;
        } else if (!this.maskedPhoneNumber && res?.data?.phoneNumber) {
          this.maskedPhoneNumber = res.data.phoneNumber;
        }

        const expSec = resData?.ExpiresInSeconds ?? resData?.expiresInSeconds;
        if (expSec != null && !isNaN(Number(expSec)) && Number(expSec) > 0) {
          this.defaultExpirationSeconds = Number(expSec);
        }

        const resendSec =
          resData?.ResendAfterSeconds ?? resData?.resendAfterSeconds;
        if (
          resendSec != null &&
          !isNaN(Number(resendSec)) &&
          Number(resendSec) >= 0
        ) {
          this.defaultResendCooldownSeconds = Number(resendSec);
        }

        this.startOtpTimers();
        this.focusOtpInput(0);
      },
      error: (err) => {
        this.isSendingOtp = false;
        const msg =
          err?.error?.Message ||
          err?.error?.data?.Message ||
          err?.error?.data?.message ||
          err?.error?.message ||
          'Shared.SOMETHING_WENT_WRONG';
        this.notificationService.error(msg);
      },
    });
  }

  private startOtpTimers(): void {
    this.clearAllTimers();

    // Validity timer
    this.expirationSeconds = this.defaultExpirationSeconds;
    this.expirationIntervalId = setInterval(() => {
      if (this.expirationSeconds > 0) {
        this.expirationSeconds--;
      } else {
        clearInterval(this.expirationIntervalId);
      }
    }, 1000);

    // Resend cooldown timer
    this.resendCooldownSeconds = this.defaultResendCooldownSeconds;
    this.resendIntervalId = setInterval(() => {
      if (this.resendCooldownSeconds > 0) {
        this.resendCooldownSeconds--;
      } else {
        clearInterval(this.resendIntervalId);
      }
    }, 1000);
  }

  private clearAllTimers(): void {
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
      this.expirationIntervalId = null;
    }
    if (this.resendIntervalId) {
      clearInterval(this.resendIntervalId);
      this.resendIntervalId = null;
    }
  }

  get isCodeExpired(): boolean {
    return this.expirationSeconds <= 0;
  }

  get canResendOtp(): boolean {
    return this.resendCooldownSeconds <= 0 && !this.isSendingOtp;
  }

  get isWrongAttemptsExceeded(): boolean {
    return this.wrongAttempts >= this.maxWrongAttempts;
  }

  get formattedExpirationTime(): string {
    const mins = Math.floor(this.expirationSeconds / 60);
    const secs = this.expirationSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  get currentOtpValue(): string {
    return this.otpDigits.join('');
  }

  get isOtpComplete(): boolean {
    return this.otpDigits.every(
      (d) => d !== '' && d !== null && d !== undefined,
    );
  }

  onOtpInput(event: any, index: number): void {
    let inputVal: string = event.target?.value || '';

    // Convert Arabic-Indic numerals to standard Western digits
    inputVal = inputVal.replace(/[٠-٩]/g, (d) =>
      '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString(),
    );
    inputVal = inputVal.replace(/\D/g, '');

    if (!inputVal) {
      this.otpDigits[index] = '';
      return;
    }

    // Handle multiple digits (e.g. mobile auto-fill)
    if (inputVal.length > 1) {
      const chars = inputVal.split('').slice(0, this.otpLength);
      for (let i = 0; i < chars.length; i++) {
        if (index + i < this.otpLength) {
          this.otpDigits[index + i] = chars[i];
        }
      }
      const nextFocus = Math.min(index + chars.length, this.otpLength - 1);
      this.focusOtpInput(nextFocus);
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
      return;
    }

    this.otpDigits[index] = inputVal;
    if (index < this.otpLength - 1) {
      this.focusOtpInput(index + 1);
    }

    if (
      this.isOtpComplete &&
      !this.isCodeExpired &&
      !this.isWrongAttemptsExceeded
    ) {
      this.verifyOtp();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    if (event.key === 'Backspace') {
      if (!this.otpDigits[index] && index > 0) {
        this.otpDigits[index - 1] = '';
        this.focusOtpInput(index - 1);
      } else {
        this.otpDigits[index] = '';
      }
    } else if (event.key === 'ArrowLeft') {
      const targetIndex =
        this.languageService.currentLanguage === 'ar' ? index + 1 : index - 1;
      if (targetIndex >= 0 && targetIndex < this.otpLength) {
        this.focusOtpInput(targetIndex);
      }
    } else if (event.key === 'ArrowRight') {
      const targetIndex =
        this.languageService.currentLanguage === 'ar' ? index - 1 : index + 1;
      if (targetIndex >= 0 && targetIndex < this.otpLength) {
        this.focusOtpInput(targetIndex);
      }
    } else if (/^[0-9]$/.test(event.key)) {
      // Overwrite the current box digit and proceed to next
      this.otpDigits[index] = event.key;
      const target = event.target as HTMLInputElement;
      if (target) {
        target.value = event.key;
      }
      if (index < this.otpLength - 1) {
        this.focusOtpInput(index + 1);
      }
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
      event.preventDefault();
    }
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipData = event.clipboardData?.getData('text') || '';
    const digitsOnly = clipData
      .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString())
      .replace(/\D/g, '')
      .slice(0, this.otpLength);

    if (digitsOnly) {
      const chars = digitsOnly.split('');
      for (let i = 0; i < this.otpLength; i++) {
        this.otpDigits[i] = chars[i] || '';
      }
      const nextFocus = Math.min(chars.length, this.otpLength - 1);
      this.focusOtpInput(nextFocus);
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
    }
  }

  private focusOtpInput(index: number): void {
    setTimeout(() => {
      const el = document.getElementById(
        `otp-input-${index}`,
      ) as HTMLInputElement;
      if (el) {
        el.focus();
        el.select();
      }
    }, 50);
  }

  verifyOtp(): void {
    if (
      !this.isOtpComplete ||
      this.isVerifyingOtp ||
      this.isCodeExpired ||
      this.isWrongAttemptsExceeded
    ) {
      return;
    }

    this.isVerifyingOtp = true;
    const otp = this.currentOtpValue;

    this.invitationService.verifyOtp(this.token, otp).subscribe({
      next: (res: any) => {
        this.isVerifyingOtp = false;

        const isFailed =
          res?.data?.errorCode != null ||
          res?.errorCode != null ||
          res?.success === false ||
          res?.Success === false ||
          res?.isSuccess === false ||
          res?.IsSuccess === false ||
          res?.isValid === false ||
          res?.IsValid === false ||
          res?.data === false ||
          res?.Status === 'Invalid' ||
          res?.Status === 'Failed' ||
          res?.Status === 'Error' ||
          res?.status === 'Invalid' ||
          res?.status === 'Failed' ||
          res?.status === 'Error';

        if (isFailed) {
          const msg =
            res?.Message ||
            res?.message ||
            res?.data?.Message ||
            res?.data?.message ||
            res?.data?.errorMessage;
          this.handleFailedOtpAttempt(msg);
          return;
        }

        // True verification success
        this.isOtpVerified = true;
        this.clearAllTimers();
        this.notificationService.success('Invitation.OTP_VERIFIED_SUCCESS');
        this.loadDetailsAndProceed();
      },
      error: (err) => {
        this.isVerifyingOtp = false;
        const msg =
          err?.error?.Message ||
          err?.error?.message ||
          err?.error?.data?.Message ||
          err?.error?.data?.message;
        this.handleFailedOtpAttempt(msg);
      },
    });
  }

  private handleFailedOtpAttempt(apiMessage?: string): void {
    this.wrongAttempts++;
    if (this.wrongAttempts >= this.maxWrongAttempts) {
      this.notificationService.error('Invitation.MAX_ATTEMPTS_EXCEEDED');
    } else {
      const remaining = this.maxWrongAttempts - this.wrongAttempts;
      const defaultMsg =
        this.languageService.currentLanguage === 'ar'
          ? `رمز التحقق غير صحيح. متبقي لديك ${remaining} محاولة.`
          : `Invalid verification code. You have ${remaining} attempt(s) remaining.`;
      this.notificationService.error(apiMessage || defaultMsg);

      // Select first box so user can immediately type new digits
      this.focusOtpInput(0);
    }
  }

  // ==========================================================
  // STEP 2: CONFIRM DETAILS
  // ==========================================================

  private loadDetailsAndProceed(): void {
    this.isLoadingDetails = true;
    this.invitationService.getDetails(this.token).subscribe({
      next: (res) => {
        this.isLoadingDetails = false;
        this.details = res?.data || res?.resource || (res as any) || {};
        this.currentStep = 2;
      },
      error: (err) => {
        this.isLoadingDetails = false;
        // Even if details call fails, allow proceeding to details step so the user isn't permanently blocked
        this.details = {};
        this.currentStep = 2;
        const msg = err?.error?.data?.message || err?.error?.message;
        if (msg) {
          this.notificationService.error(msg);
        }
      },
    });
  }

  get displayedNationalId(): string {
    return (
      this.details.NationalId ||
      this.details.NationalID ||
      this.details.IdentityNumber ||
      this.details.nationalId ||
      this.details.nationalID ||
      this.details.identityNumber ||
      '—'
    );
  }

  get displayedEmail(): string {
    return (
      this.details.Email ||
      this.details.EmailAddress ||
      this.details.email ||
      this.details.emailAddress ||
      '—'
    );
  }

  get displayedBirthDate(): string {
    const raw =
      this.details.BirthDate ||
      this.details.DateOfBirth ||
      this.details.birthDate ||
      this.details.dateOfBirth;
    if (!raw) return '—';
    try {
      const date = new Date(raw);
      if (isNaN(date.getTime())) return raw.toString();
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return raw.toString();
    }
  }

  get displayedPhone(): string {
    return (
      this.details.PhoneNumber ||
      this.details.MobileNumber ||
      this.details.Phone ||
      this.details.MaskedPhone ||
      this.details.phoneNumber ||
      this.details.mobileNumber ||
      this.details.phone ||
      this.maskedPhoneNumber ||
      '—'
    );
  }

  proceedToPasswordStep(): void {
    this.currentStep = 3;
  }

  // ==========================================================
  // STEP 3: CREATE PASSWORD & ACTIVATE
  // ==========================================================

  private initPasswordForm(): void {
    this.passwordForm = new FormGroup(
      {
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(JariValidators.validPasswordRegex),
        ]),
        confirmPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
        ]),
      },
      { validators: this.passwordMatchValidator.bind(this) },
    );

    this.passwordForm.get('password')?.valueChanges.subscribe((val: string) => {
      this.evaluatePasswordRules(val || '');
    });

    this.passwordForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      const pass = this.passwordForm.get('password')?.value || '';
      const confirm = this.passwordForm.get('confirmPassword')?.value || '';
      this.passwordRules.matches = !!pass && pass === confirm;
    });
  }

  private evaluatePasswordRules(val: string): void {
    this.passwordRules.minChars = val.length >= 8;
    this.passwordRules.hasCapital = /[A-Z]/.test(val);
    this.passwordRules.hasNumber = /\d/.test(val);
    this.passwordRules.hasSpecial = /[@$!%*?&]/.test(val);
    const confirm = this.passwordForm.get('confirmPassword')?.value || '';
    this.passwordRules.matches = !!val && val === confirm;
  }

  private passwordMatchValidator(
    group: AbstractControl,
  ): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (!pass || !confirm) return null;
    return pass === confirm ? null : { passwordNotMatch: true };
  }

  activateAccount(): void {
    if (this.isActivating || !this.acceptTerms || this.passwordForm.invalid) {
      return;
    }

    this.isActivating = true;
    const password = this.passwordForm.get('password')?.value;

    this.invitationService
      .activate({
        token: this.token,
        password: password,
        acceptTerms: true,
      })
      .subscribe({
        next: (res) => {
          this.isActivating = false;
          const dataAny = res?.data as any;
          if (dataAny?.errorCode != null || res?.success === false) {
            const msg =
              dataAny?.message || res?.message || 'Shared.SOMETHING_WENT_WRONG';
            this.notificationService.error(msg);
            return;
          }

          this.notificationService.success('Invitation.ACTIVATION_SUCCESS');

          // Save sign-in data
          if (res?.data) {
            this.userService.currentUserCredantials = res.data;

            if (res.data.currentUsedInvestorAccountId) {
              this.signalrService.startConnection(
                res.data.currentUsedInvestorAccountId,
              );
            }

            const targetRoute =
              res.data.isCompleteProfile === true ||
              res.data.userKYCStatus === 'Approved' ||
              res.data.userKYCStatus === 'Pending'
                ? ['/opportunities/opportunity-list']
                : ['/kyc/kyc-questions'];

            this.router
              .navigate(targetRoute)
              .then((navigated) => {
                if (!navigated) {
                  this.router.navigate(['/opportunities/opportunity-list']);
                }
              })
              .catch(() => {
                this.router.navigate(['/opportunities/opportunity-list']);
              });
          } else {
            this.router.navigate(['/authentication/login']);
          }
        },
        error: (err) => {
          this.isActivating = false;
          const msg =
            err?.error?.data?.message ||
            err?.error?.message ||
            'Shared.SOMETHING_WENT_WRONG';
          this.notificationService.error(msg);
        },
      });
  }

  // ==========================================================
  // TERMS AND CONDITIONS MODAL
  // ==========================================================

  openTermsModal(): void {
    this.isLoadingTerms = true;
    this.termsDialogRefInstance = this.matDialog.open(this.termsDialogRef, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '85vh',
    });

    this.faqDataService.TermsAndConditions().subscribe({
      next: (res) => {
        this.isLoadingTerms = false;
        this.termsData = res?.data;
      },
      error: () => {
        this.isLoadingTerms = false;
      },
    });
  }

  agreeToTermsFromModal(): void {
    this.acceptTerms = true;
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  closeTermsModal(): void {
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  // ==========================================================
  // SUPPORT CONTACTS HELPERS
  // ==========================================================

  get supportEmail(): string {
    return (
      this.invitationData?.SupportEmail ||
      this.invitationData?.supportEmail ||
      this.tenantConfigService.tenant.supportEmail ||
      'info@miyarcapital.com'
    );
  }

  get supportPhone(): string {
    return (
      this.invitationData?.SupportPhone ||
      this.invitationData?.supportPhone ||
      this.tenantConfigService.tenant.tollFreeNumber ||
      '920 032 099'
    );
  }

  private formatSupportMessage(
    rawMessage: string,
    type: 'Expired' | 'Cancelled' | 'Invalid',
  ): string {
    let msg = rawMessage || this.getFallbackMessage(type);
    msg = msg.replace(/\{SUPPORT_EMAIL[\)\}]/gi, this.supportEmail);
    msg = msg.replace(/\{SUPPORT_PHONE[\)\}]/gi, this.supportPhone);
    return msg;
  }

  private getFallbackMessage(
    type: 'Expired' | 'Cancelled' | 'Invalid',
  ): string {
    const isAr = this.languageService.currentLanguage === 'ar';
    if (type === 'Expired') {
      return isAr
        ? `انتهت صلاحية هذه الدعوة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
        : `This invitation has expired. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
    }
    if (type === 'Cancelled') {
      return isAr
        ? `هذه الدعوة لم تعد صالحة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
        : `This invitation is no longer valid. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
    }
    return isAr
      ? `هذه الدعوة غير صالحة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
      : `This invitation is no longer valid. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
  }
}
